import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

// Public because it must accept reports from the published site and the native
// shells without a session. It only writes to the server log — it never reads
// or returns any wallet data, and it stores nothing.
const reportSchema = z.object({
  kind: z.enum(["crash", "transaction"]),
  message: z.string().max(500),
  route: z.string().max(200).optional(),
  stack: z.string().max(6_000).optional(),
  mechanism: z.string().max(60).optional(),
  release: z.string().max(120).optional(),
  address: z.string().max(60).optional(),
  txType: z.string().max(60).optional(),
  resultCode: z.string().max(60).optional(),
  txHash: z.string().max(80).optional(),
  userAgent: z.string().max(300).optional(),
});

const MAX_BODY_BYTES = 16_000;

export const Route = createFileRoute("/api/public/client-report")({
  staticData: { sitemap: false },
  server: {
    handlers: {
      POST: async ({ request }) => {
        const raw = await request.text();
        if (raw.length > MAX_BODY_BYTES) return new Response("Payload too large", { status: 413 });

        let parsed: z.infer<typeof reportSchema>;
        try {
          parsed = reportSchema.parse(JSON.parse(raw));
        } catch {
          return new Response("Invalid report", { status: 400 });
        }

        const tag = parsed.kind === "crash" ? "COBALT_CRASH" : "COBALT_TX_FAILURE";
        console.error(
          `[${tag}] ${parsed.message}`,
          JSON.stringify({
            route: parsed.route,
            mechanism: parsed.mechanism,
            release: parsed.release,
            address: parsed.address,
            txType: parsed.txType,
            resultCode: parsed.resultCode,
            txHash: parsed.txHash,
            userAgent: parsed.userAgent,
            at: new Date().toISOString(),
          }),
          parsed.stack ? `\n${parsed.stack}` : "",
        );

        return new Response(null, { status: 204 });
      },
    },
  },
});
